import { RefObject, useCallback, useEffect, useRef, useLayoutEffect, MutableRefObject } from 'react';

import {
    findBestFitPlacement,
    getMetrics,
    applyPlacementToDrop,
} from '@hh.ru/magritte-internal-drop-base/findBestFitPlacement';
import type {
    ArrowGuardDistance,
    ActivatorElementType,
    Placement,
    RefCallbackFactory,
} from '@hh.ru/magritte-internal-drop-base/types';
import { useMonitorResize } from '@hh.ru/magritte-internal-drop-base/useMonitorResize';

let isInitialized = false;

interface SubscriptionCallback {
    (target: HTMLElement): void;
}

const scrollSubscriptions: Array<SubscriptionCallback> = [];
const canceledScrollSubscriptions: Map<HTMLElement, Array<SubscriptionCallback>> = new Map();

const scrollHandler = (event: Event) => {
    const target = event.target as HTMLElement;
    if (!target) {
        return;
    }

    if (scrollSubscriptions.length === 0) {
        return;
    }
    const canceledSubscriptions = canceledScrollSubscriptions.get(target);

    const subscriptions =
        canceledSubscriptions === undefined
            ? scrollSubscriptions
            : scrollSubscriptions.filter((subscription) => !canceledSubscriptions.includes(subscription));

    subscriptions.forEach((subscription) => subscription(target));
};

const initialize = () => {
    if (isInitialized) {
        return;
    }

    isInitialized = true;
    document.addEventListener('scroll', scrollHandler, { capture: true, passive: false });
};

const suspendSubscription = (callback: SubscriptionCallback): void => {
    const index = scrollSubscriptions.indexOf(callback);
    if (index !== -1) {
        scrollSubscriptions.splice(index, 1);
    }
};

const reviveSubscription = (callback: SubscriptionCallback): void => {
    initialize();
    scrollSubscriptions.push(callback);
};

export const deleteScrollSubscription = (callback: SubscriptionCallback): void => {
    const index = scrollSubscriptions.indexOf(callback);
    if (index !== -1) {
        scrollSubscriptions.splice(index, 1);
    }
    for (const [key, callbacks] of canceledScrollSubscriptions.entries()) {
        const index = callbacks.indexOf(callback);
        if (index !== -1) {
            canceledScrollSubscriptions.set(key, callbacks.splice(index, 1));
        }
    }
};

const unsubscribeFromTarget = (target: HTMLElement, callback: SubscriptionCallback): void => {
    if (!canceledScrollSubscriptions.has(target)) {
        canceledScrollSubscriptions.set(target, []);
    }
    canceledScrollSubscriptions.get(target)?.push(callback);
};

export const usePositionManager = (
    activatorRef: RefObject<ActivatorElementType>,
    dropRef: RefObject<HTMLElement>,
    isVisible: boolean,
    placement: Placement | Placement[],
    customViewport: RefObject<HTMLElement> | undefined,
    allowShrinkHeightToFitIntoViewport: boolean,
    widthEqualToActivator: boolean,
    arrowGuardDistance: ArrowGuardDistance,
    guardDistanceToActivator: number,
    guardDistanceToViewport: number,
    minDropHeight: number,
    forceDropMetricsUpdate: MutableRefObject<VoidFunction> | undefined,
    lockPlacement = false
): {
    resizeRefCallbackFactory: RefCallbackFactory;
    bestFitPlacementRect: DOMRect | undefined;
} => {
    const bestFitPlacementRef = useRef<DOMRect>();
    const dropMetricsRef = useRef<DOMRect>();

    const prefferedPlacementRef = useRef<Placement>();

    const updateMetricsSyncRef = useRef<VoidFunction>(() => void 0);

    const { getResizeObserver, observeResizeRefCallbackFactory, getObservedElementsInfoMap } = useMonitorResize(
        dropMetricsRef,
        updateMetricsSyncRef
    );

    const getDropMetrics = useCallback(
        (activatorRect: DOMRect, widthEqualToActivator: boolean) => {
            if (dropMetricsRef.current) {
                return dropMetricsRef.current;
            }
            if (!dropRef.current) {
                return new DOMRect();
            }

            const scrollPositions: { node: HTMLElement; top: number; left: number }[] = [];
            for (const info of getObservedElementsInfoMap().values()) {
                if (info.isScrollable && info.node) {
                    scrollPositions.push({
                        node: info.node,
                        top: info.node.scrollTop,
                        left: info.node.scrollLeft,
                    });
                }
            }

            // Ширина не должна превышать максимально допустимую, т.к. это может повлиять на высоту, например из-за
            // переноса текста на новую строку.
            dropRef.current.style.width = widthEqualToActivator ? `${activatorRect.width}px` : '';
            dropRef.current.style.height = '';
            dropMetricsRef.current = dropRef.current.getBoundingClientRect();
            if (bestFitPlacementRef.current) {
                applyPlacementToDrop(dropRef, bestFitPlacementRef.current);
            }
            for (const { node, top, left } of scrollPositions) {
                node.scrollTop = top;
                node.scrollLeft = left;
            }
            return dropMetricsRef.current;
        },
        [dropRef, getObservedElementsInfoMap]
    );

    updateMetricsSyncRef.current = () => {
        if (!dropRef.current || !isVisible) {
            bestFitPlacementRef.current = undefined;
            return;
        }
        const { activatorRect, viewportRect, offsetParentRect } = getMetrics({
            activatorRef,
            customViewport,
            dropRef,
        });
        const dropRect = getDropMetrics(activatorRect, widthEqualToActivator);
        const {
            rect,
            parsedPlacement: choosenPlacement,
            placement: prefferedPlacement,
        } = findBestFitPlacement({
            placements: Array.isArray(placement) ? placement : [placement],
            activatorRect,
            viewportRect,
            offsetParentRect,
            dropWidth: dropRect.width,
            dropHeight: dropRect.height,
            guardDistanceToActivator,
            guardDistanceToViewport,
            minDropHeight,
            widthEqualToActivator,
            allowShrinkHeightToFitIntoViewport,
            arrowGuardDistance,
            prefferedPlacement: prefferedPlacementRef.current,
            lockPlacement,
        });
        prefferedPlacementRef.current = prefferedPlacement;
        applyPlacementToDrop(dropRef, rect);
        dropRef.current.dataset.magritteDropBaseDirection = choosenPlacement.direction;
        dropRef.current.dataset.magritteDropBaseAlignment = choosenPlacement.alignment;
        bestFitPlacementRef.current = rect;
    };

    if (forceDropMetricsUpdate) {
        forceDropMetricsUpdate.current = () => {
            dropMetricsRef.current = undefined;
            updateMetricsSyncRef.current();
        };
    }

    useEffect(() => {
        if (!isVisible) {
            prefferedPlacementRef.current = undefined;
            return () => void 0;
        }
        const activatorElement = activatorRef.current;
        const customViewportElement = customViewport?.current;
        const resizeHandler = () => updateMetricsSyncRef.current();
        activatorElement && getResizeObserver().observe(activatorElement);
        customViewportElement && getResizeObserver().observe(customViewportElement);
        window.addEventListener('resize', resizeHandler);
        return () => {
            activatorElement && getResizeObserver().unobserve(activatorElement);
            customViewportElement && getResizeObserver().unobserve(customViewportElement);
            window.removeEventListener('resize', resizeHandler);
        };
    }, [activatorRef, getResizeObserver, customViewport, isVisible]);

    useLayoutEffect(() => {
        isVisible && updateMetricsSyncRef.current();
    }, [isVisible, placement]);

    /**
     * список id скроллящихся элементов для которых была выполнена проверка
     * влияет ли их скролл на взаимное положение активатора и дропа.
     * Получение сделано с помощью отдельной функции чтобы не создавать сет на каждый ререндер.
     */
    const processedScrollContainers = useRef<Set<HTMLElement>>();
    const getProcessedScrollContainers = (): Set<HTMLElement> => {
        processedScrollContainers.current = processedScrollContainers.current || new Set();
        return processedScrollContainers.current;
    };

    const visibleRef = useRef(isVisible);
    visibleRef.current = isVisible;

    const scrollHandler = useCallback(
        (target: HTMLElement) => {
            if (!visibleRef.current || !dropRef.current || !activatorRef.current) {
                return;
            }

            // Если это первый скролл данного контейнера, то проверяем содержит ли он активатор
            // Если он не содержит активатор, то можно отписаться от дальнейших оповещений о его скролле.
            if (!getProcessedScrollContainers().has(target)) {
                if (!target.contains(activatorRef.current)) {
                    unsubscribeFromTarget(target, scrollHandler);
                    return;
                }
                getProcessedScrollContainers().add(target);
            }

            updateMetricsSyncRef.current();
        },
        [activatorRef, dropRef]
    );

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        isVisible ? reviveSubscription(scrollHandler) : suspendSubscription(scrollHandler);
    }, [isVisible, scrollHandler]);

    useEffect(() => {
        return () => deleteScrollSubscription(scrollHandler);
    }, [scrollHandler]);

    return {
        resizeRefCallbackFactory: observeResizeRefCallbackFactory,
        bestFitPlacementRect: bestFitPlacementRef.current,
    };
};
